import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { styled } from '@mui/material/styles';
import TableCell, { tableCellClasses } from '@mui/material/TableCell';
import { Table, TableBody, Typography, TableContainer, TableHead, TableRow, Paper, Stack, Pagination, Box, Chip, Button } from '@mui/material';
import { setMainHeading } from '../../../../redux/modules/mainHeading/slice';
import { getLabOrderRequest } from '../../../../redux/modules/laboratory/labOrder/slice';
import { useNavigate, useLocation } from 'react-router-dom';
import WriteGuard from '../../../../components/auth/WriteGuard';

const StyledTableCell = styled(TableCell)(({ theme }) => ({
    [`&.${tableCellClasses.head}`]: { backgroundColor: theme.palette.primary.main, color: theme.palette.common.white },
    [`&.${tableCellClasses.body}`]: { fontSize: 14 },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
    '&:nth-of-type(odd)': { backgroundColor: theme.palette.action.hover },
    '&:last-child td, &:last-child th': { border: 0 },
}));

const priorityColors = { routine: 'default', urgent: 'warning', stat: 'error' };

let limit = 10;

export default function AuthorizationQueue() {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const location = useLocation();
    const basePath = location.pathname.split('/laboratory')[0];
    let { getRes } = useSelector(state => state.labOrder);
    let { currentBranch } = useSelector(state => state.branch);
    const [currentPage, setCurrentPage] = useState(1);

    useEffect(() => {
        dispatch(setMainHeading("Authorization Queue"));
    }, [dispatch]);

    useEffect(() => {
        if (!currentBranch) return;
        dispatch(getLabOrderRequest({ page: currentPage, limit, status: 'resulted' }));
    }, [dispatch, currentBranch, currentPage]);

    const pageCount = getRes?.dataCount > limit ? Math.ceil(getRes.dataCount / limit) : 0;

    return (
        <>
            <Typography variant="body2" color="text.secondary" mb={3} mt={4}>
                Orders with results awaiting pathologist authorization
            </Typography>
            <Box sx={{ overflowX: { xs: 'auto', md: 'visible' }, mx: { xs: -2, sm: 0 }, px: { xs: 2, sm: 0 } }}>
                <TableContainer component={Paper} className="tableShadow">
                    <Table sx={{ minWidth: { xs: 400, md: 700 } }} className="commonTable">
                        <TableHead>
                            <TableRow>
                                <StyledTableCell>Order ID</StyledTableCell>
                                <StyledTableCell>Patient</StyledTableCell>
                                <StyledTableCell>Tests</StyledTableCell>
                                <StyledTableCell>Priority</StyledTableCell>
                                <StyledTableCell>Ordered</StyledTableCell>
                                <StyledTableCell align="right">Action</StyledTableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {getRes?.data?.length > 0 ? getRes.data.map(order => (
                                <StyledTableRow key={order._id}>
                                    <StyledTableCell sx={{ fontWeight: 600 }}>{order.labOrderId}</StyledTableCell>
                                    <StyledTableCell>
                                        {order.patientId ? `${order.patientId.fName || ''} ${order.patientId.lName || ''}`.trim() : '-'}
                                    </StyledTableCell>
                                    <StyledTableCell>
                                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                            {order.tests?.slice(0, 4).map((t, i) => (
                                                <Chip key={i} label={t.testId?.testCode || '?'} size="small" variant="outlined" />
                                            ))}
                                            {order.tests?.length > 4 && <Chip label={`+${order.tests.length - 4}`} size="small" />}
                                        </Box>
                                    </StyledTableCell>
                                    <StyledTableCell>
                                        <Chip label={order.priority} size="small" color={priorityColors[order.priority] || 'default'} sx={{ textTransform: 'capitalize' }} />
                                    </StyledTableCell>
                                    <StyledTableCell>{order.orderedAt ? new Date(order.orderedAt).toLocaleDateString() : '-'}</StyledTableCell>
                                    <StyledTableCell align="right">
                                        <WriteGuard capability="labResultValidation:write">
                                            <Button size="small" variant="contained" color="success" onClick={() => navigate(`${basePath}/laboratory/results/entry/${order._id}`)}>
                                                Review & Authorize
                                            </Button>
                                        </WriteGuard>
                                    </StyledTableCell>
                                </StyledTableRow>
                            )) : (
                                <StyledTableRow>
                                    <StyledTableCell colSpan={6}><Typography>No orders pending authorization</Typography></StyledTableCell>
                                </StyledTableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Box>
            {pageCount > 0 && <Pagination count={pageCount} page={currentPage} onChange={(e, v) => setCurrentPage(v)} />}
        </>
    );
}
